import React, { useCallback, useEffect, useMemo, useState } from 'react';
import EmptyState from '../EmptyState';
import Button from '../ui/Button';
import LoadErrorBox from '../ui/LoadErrorBox';
import { useConfirm } from '../ui/ConfirmProvider';
import { useNotifications } from '../NotificationProvider';
import { useRole } from '../../hooks/useRole';
import { useProject } from '../../contexts/ProjectContext';
import { usePageFilter } from '../../contexts/PageFilterContext';
import { archiveGuest, loadGuests, refreshGuestsFromApi } from '../../services/GuestService';
import { loadProjectRsvpState, type ProjectRsvp } from '../../services/GuestEventsService';
import { loadEvents, refreshEventsFromApi } from '../../services/EventsService';
import { saveBulkRsvps, type BulkFailure, type ConflictGuest } from '../../services/GuestRsvpBulkService';
import {
  answersFor, baselineFrom, buildBulkChanges, stampsFrom, changedEvents, confirmInvited, countChangedGuests,
  guestMatchesFilter, inviteAll, inviteAllBut, lastFour, nextAnswer, type Baseline, type EventAnswers, type Stamps, type RsvpFilter,
} from '../../utils/bulkRsvp';
import { effectiveRsvpStatus } from '../../utils/guestRsvpStatus';
import type { Guest } from '../../types/Guests';
import BulkRsvpCard, { type CardEvent } from './BulkRsvpCard';
import BulkRsvpConflicts, { type OpenConflict, type Resolution } from './BulkRsvpConflicts';
import '../../styles/components/BulkRsvp.css';

type Phase = 'loading' | 'ready' | 'error';
/** What the server told us about a guest during a conflict: replaces the loaded state of that guest. */
interface Fresh { events?: EventAnswers; updatedAt?: string; status?: string }

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const omit = <T,>(rec: Readonly<Record<string, T>>, ids: ReadonlySet<string> | readonly string[]): Record<string, T> => {
  const drop = new Set(ids);
  return Object.fromEntries(Object.entries(rec).filter(([k]) => !drop.has(k)));
};

/** Guests > RSVPs: set every guest's answer for every event of the project on one screen, then save the lot. */
const BulkRsvpEditor: React.FC = () => {
  const { isPlanner } = useRole();
  const { activeProjectId } = useProject();
  const confirm = useConfirm();
  const { showSuccess, showError, showWarning } = useNotifications();
  const search = usePageFilter().trim().toLowerCase();

  const [phase, setPhase] = useState<Phase>('loading');
  const [loadError, setLoadError] = useState<unknown>(null);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [rsvps, setRsvps] = useState<ProjectRsvp[]>([]);
  const [serverStamps, setServerStamps] = useState<Stamps>(new Map());
  const [events, setEvents] = useState<CardEvent[]>([]);
  const [drafts, setDrafts] = useState<Record<string, EventAnswers>>({});
  const [removed, setRemoved] = useState<ReadonlySet<string>>(new Set());
  const [fresh, setFresh] = useState<Record<string, Fresh>>({});
  const [filter, setFilter] = useState<RsvpFilter>('everyone');
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState<OpenConflict[]>([]);
  const [conflictTotal, setConflictTotal] = useState(0);
  const [resolutions, setResolutions] = useState<Record<string, Resolution | undefined>>({});
  const [problem, setProblem] = useState<string | null>(null);

  const load = useCallback(async () => {
    setPhase('loading');
    setLoadError(null);
    try {
      const [, state, evs] = await Promise.all([refreshGuestsFromApi(), loadProjectRsvpState(), refreshEventsFromApi({ strict: true })]);
      setGuests(loadGuests().filter(g => g.archived !== true));
      setRsvps(state.rsvps);
      setServerStamps(stampsFrom(state.guests));
      setEvents(evs.filter(e => !e.archived).sort((a, b) => a.order - b.order).map(e => ({ id: e.id, title: e.title })));
      setPhase('ready');
    } catch (err) {
      setLoadError(err);
      setPhase('error');
    }
  }, []);
  useEffect(() => { if (isPlanner) void load(); }, [load, isPlanner, activeProjectId]);

  /** Quiet re-read after a save: the cards then show the saved answers and the status the server derived. */
  const refresh = useCallback(async () => {
    try {
      const [, state] = await Promise.all([refreshGuestsFromApi(), loadProjectRsvpState()]);
      setGuests(loadGuests().filter(g => g.archived !== true));
      setRsvps(state.rsvps);
      setServerStamps(stampsFrom(state.guests));
      setFresh({});
    } catch {
      showWarning('Saved, but the list could not be refreshed. Reload the page before editing these guests again.');
    }
  }, [showWarning]);

  const eventIds = useMemo(() => events.map(e => e.id), [events]);
  const baseline: Baseline = useMemo(() => {
    const b = baselineFrom(rsvps);
    for (const [id, f] of Object.entries(fresh)) if (f.events) b.set(id, Object.fromEntries(Object.entries(f.events).filter(([, a]) => a !== null)) as EventAnswers);
    return b;
  }, [rsvps, fresh]);
  const stamps = useMemo(() => {
    const t = new Map(serverStamps);
    for (const [id, f] of Object.entries(fresh)) if (f.updatedAt) t.set(id, f.updatedAt);
    return t;
  }, [serverStamps, fresh]);
  const ctx = useMemo(() => ({ baseline, drafts, removed, eventIds }), [baseline, drafts, removed, eventIds]);

  const answersOf = useCallback((id: string): EventAnswers => drafts[id] ?? answersFor(baseline.get(id), eventIds), [drafts, baseline, eventIds]);
  const edit = useCallback((id: string, fn: (a: EventAnswers) => EventAnswers) => setDrafts(d => ({ ...d, [id]: fn(d[id] ?? answersFor(baseline.get(id), eventIds)) })), [baseline, eventIds]);

  const changedCount = countChangedGuests(ctx);
  const filterCounts = useMemo(() => ({
    pending: guests.filter(g => guestMatchesFilter('pending', g.id, ctx)).length,
    everyone: guests.length,
    mine: guests.filter(g => guestMatchesFilter('mine', g.id, ctx)).length,
  }), [guests, ctx]);
  const visible = useMemo(() => guests.filter(g => {
    if (!guestMatchesFilter(filter, g.id, ctx)) return false;
    if (!search) return true;
    const digits = search.replace(/\D/g, '');
    return g.name.toLowerCase().includes(search) || g.id.toLowerCase().includes(search) || (digits.length >= 2 && (g.phone ?? '').replace(/\D/g, '').includes(digits));
  }), [guests, filter, ctx, search]);

  const nameOf = useCallback((id: string) => guests.find(g => g.id === id)?.name ?? id, [guests]);

  const handleConflict = (failure: Extract<BulkFailure, { kind: 'conflict' }>, savedNote: string) => {
    const open: OpenConflict[] = failure.conflicts.map((c: ConflictGuest) => ({
      theirs: c, name: c.name ?? nameOf(c.guestId),
      mine: changedEvents(answersFor(baseline.get(c.guestId), eventIds), drafts[c.guestId] ?? answersFor(baseline.get(c.guestId), eventIds)),
    }));
    setConflicts(open);
    setConflictTotal(failure.total);
    setResolutions({});
    setProblem(open.length === 0 ? `${failure.message} ${savedNote}`.trim() : null);
  };

  const resolve = (id: string, choice: Resolution) => {
    const c = conflicts.find(x => x.theirs.guestId === id);
    if (!c) return;
    const t = c.theirs;
    setResolutions(r => ({ ...r, [id]: choice }));
    setFresh(f => ({ ...f, [id]: { events: t.events, updatedAt: t.updatedAt, status: t.status } }));
    if (choice === 'theirs') {
      setDrafts(d => omit(d, [id]));
      if (!t.events) void refresh();
    } else if (t.events) {
      // their fresh answers underneath, my changes on top: what they changed and I did not touch is not overwritten
      setDrafts(d => ({ ...d, [id]: { ...answersFor(Object.fromEntries(Object.entries(t.events!).filter(([, a]) => a !== null)) as EventAnswers, eventIds), ...c.mine } }));
    }
  };

  const save = async () => {
    const stagedIds = guests.filter(g => removed.has(g.id));
    if (stagedIds.length > 0 && !(await confirm({
      title: `Archive ${plural(stagedIds.length, 'guest', 'guests')}?`,
      description: `${stagedIds.map(g => g.name).join(', ')} will leave the guest list. They can be restored from Archived.`,
      confirmLabel: 'Archive',
    }))) return;
    const changes = buildBulkChanges({ guests, baseline, stamps, drafts, removed, eventIds });
    setSaving(true);
    setProblem(null);
    setConflicts([]);
    const result = await saveBulkRsvps(changes);
    if (result.savedGuestIds.length > 0) {
      const done = new Set(result.savedGuestIds);
      setFresh(f => ({ ...f, ...Object.fromEntries(result.savedGuestIds.map(id => [id, { ...f[id], events: answersOf(id) }])) }));
      setDrafts(d => omit(d, done));
    }
    const savedNote = result.savedCount > 0 ? `${plural(result.savedCount, 'guest was', 'guests were')} saved before this stopped.` : '';
    if (result.failure) {
      const f = result.failure;
      if (f.kind === 'conflict') handleConflict(f, savedNote);
      else if (f.kind === 'checked_in') setProblem(`${f.guestIds.map(nameOf).join(', ')} already checked in, so an invitation cannot be removed. Nothing in that batch was saved. ${savedNote}`.trim());
      else if (f.kind === 'unknown') setProblem(`${[...f.guestIds.map(nameOf), ...f.eventIds.map(id => events.find(e => e.id === id)?.title ?? 'an event')].join(', ')} no longer exist. Reload the page. ${savedNote}`.trim());
      else setProblem(`${f.message} ${savedNote}`.trim());
      setSaving(false);
      return;
    }
    if (result.savedCount > 0) showSuccess(`Saved ${plural(result.savedCount, 'guest', 'guests')}.`);
    const archived: string[] = [];
    const errors: string[] = [];
    for (const g of stagedIds) {
      try { await archiveGuest(g.id); archived.push(g.id); } catch { errors.push(`${g.name} could not be archived.`); }
    }
    if (archived.length > 0) showSuccess(`Archived ${plural(archived.length, 'guest', 'guests')}.`);
    if (errors.length > 0) showError(errors.join(' '));
    setRemoved(r => new Set([...r].filter(id => !archived.includes(id))));
    setDrafts(d => omit(d, archived));
    await refresh();
    setSaving(false);
  };

  if (!isPlanner) return <div className="gst-empty" role="alert"><p>Only planners can change RSVPs.</p></div>;
  if (phase === 'loading') return <div className="rsvp-page"><p className="rsvp-loading" role="status">Loading guests and events…</p></div>;
  if (phase === 'error') return <div className="rsvp-page"><LoadErrorBox what="guests and events" error={loadError} onRetry={() => void load()} /></div>;
  if (events.length === 0) return <div className="rsvp-page"><EmptyState icon="calendar" title="No events yet" description="Add the events of this project first, then set who is invited to each." /></div>;
  if (guests.length === 0) return <div className="rsvp-page"><EmptyState icon="users" title="No guests yet" description="Add guests first, then set their answers here." /></div>;

  const stagedGuests = guests.filter(g => removed.has(g.id));
  const unresolved = conflicts.length > 0;
  const FILTERS: Array<{ key: RsvpFilter; label: string }> = [
    { key: 'pending', label: 'Pending' }, { key: 'everyone', label: 'Everyone' }, { key: 'mine', label: 'My changes' },
  ];
  return (
    <div className="rsvp-page fade-in">
      <div className="rsvp-filters" role="group" aria-label="Show">
        {FILTERS.map(f => (
          <Button key={f.key} size="sm" variant={filter === f.key ? 'primary' : 'outline'} aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
            {f.label} ({filterCounts[f.key]})
          </Button>
        ))}
      </div>

      {unresolved && <BulkRsvpConflicts conflicts={conflicts} resolutions={resolutions} events={events} saving={saving} total={conflictTotal} onResolve={resolve} onSaveAgain={() => void save()} />}
      {problem && <p className="rsvp-problem" role="alert">{problem}</p>}

      {stagedGuests.length > 0 && (
        <section className="rsvp-removals" aria-label="Staged for removal">
          <h2 className="rsvp-removals__title">Staged for removal ({stagedGuests.length})</h2>
          <p className="rsvp-removals__note">Nothing is removed until you save. Saving archives these guests; they can be restored from Archived.</p>
          <ul>
            {stagedGuests.map(g => (
              <li key={g.id}>{g.name} <button type="button" className="rsvp-removals__undo" onClick={() => setRemoved(r => new Set([...r].filter(id => id !== g.id)))}>Undo</button></li>
            ))}
          </ul>
        </section>
      )}

      {visible.length === 0 ? (
        <EmptyState
          icon="users" compact
          title={filter === 'mine' && !search ? 'No changes yet' : filter === 'pending' && !search ? 'No guests with a pending answer' : 'No guests match'}
          description={filter === 'mine' && !search ? 'Tap an event on a guest to start.' : 'Switch to Everyone to see all guests.'}
        />
      ) : (
        <ul className="rsvp-list">
          {visible.map(g => {
            const answers = answersOf(g.id);
            const diff = changedEvents(answersFor(baseline.get(g.id), eventIds), answers);
            return (
              <BulkRsvpCard
                key={g.id} guest={g} events={events} answers={answers} changedEventIds={new Set(Object.keys(diff))}
                status={fresh[g.id]?.status ?? effectiveRsvpStatus(g)} staged={removed.has(g.id)}
                onCycle={ev => edit(g.id, a => ({ ...a, [ev]: nextAnswer(a[ev] ?? null) }))}
                onAll={() => edit(g.id, a => inviteAll(a, eventIds))}
                onAllBut={ev => edit(g.id, a => inviteAllBut(a, eventIds, ev))}
                onConfirmInvited={() => edit(g.id, a => confirmInvited(a))}
                onToggleRemove={on => setRemoved(r => (on ? new Set([...r, g.id]) : new Set([...r].filter(id => id !== g.id))))}
              />
            );
          })}
        </ul>
      )}

      <div className="rsvp-footer" role="region" aria-label="Save changes">
        <span className="rsvp-footer__count">
          {changedCount === 0 ? 'No changes' : `${plural(changedCount, 'guest', 'guests')} changed`}
          {removed.size > 0 && ` · ${removed.size} to remove`}
        </span>
        <Button variant="ghost" disabled={saving || (changedCount === 0 && !unresolved)} onClick={() => { setDrafts({}); setRemoved(new Set()); setConflicts([]); setProblem(null); setFresh({}); }}>Discard</Button>
        <Button variant="primary" loading={saving} disabled={changedCount === 0 || unresolved} onClick={() => void save()}>Save</Button>
      </div>
    </div>
  );
};

export default BulkRsvpEditor;
